/**
 * PATH: packages/email-hub/src/feature/components/KeyboardShortcutsPanel.tsx
 * FILE_ID: EMAILHUB-FEATURE-SHORTCUTS-PANEL-001
 * PURPOSE: A bottom-of-inbox modal that shows the keyboard shortcuts for THIS
 *          page (Email Hub) plus the app-wide ones, and lets the operator
 *          REBIND the editable (app-wide) shortcuts inline — e.g. move the
 *          command palette off ⌘K onto ⌘E.
 * WHY:     Operator 2026-06-23 (with screenshot): "show somewhere in the bottom
 *          the shortcut key settings that involve this page and over all app
 *          like command e so we can change it here if so we wish." The full
 *          shortcut system already exists (Settings → Shortcuts) but was only
 *          reachable from Settings; this surfaces it right on the inbox.
 * FLOW:    open → useShortcutOverrides (load/persist) → ShortcutCustomizer
 *          (editable) + HubShortcutsReference (read-only reference, Email tab).
 * DEPENDENCIES:
 *   - @quotech/ui: ShortcutCustomizer + HubShortcutsReference (presentational,
 *     prop-driven — no provider context needed).
 *   - @quotech/tenant-shared/settings/useShortcutOverrides: the SAME persistence
 *     hook Settings uses, so a rebind here shows up there and vice-versa.
 * RELATED:
 *   - feature/pages/InboxPage.tsx: renders the footer trigger + this panel.
 */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Keyboard, X } from 'lucide-react'
import { HubShortcutsReference, ShortcutCustomizer } from '@quotech/ui'
import { useShortcutOverrides } from '@quotech/tenant-shared/settings/useShortcutOverrides'

import { cn } from '../../_internal/lib/utils'

export interface KeyboardShortcutsPanelProps {
  open: boolean
  onClose: () => void
}

/**
 * KeyboardShortcutsPanel(open, onClose) → a centered modal (portalled to
 * <body>) with the editable app-wide shortcuts on top and the full per-hub
 * reference below, defaulting to the Email Hub tab. Renders null when closed.
 */
export function KeyboardShortcutsPanel({ open, onClose }: KeyboardShortcutsPanelProps) {
  const { overrides, loading, error, remap, setEnabled, reset, resetAll } =
    useShortcutOverrides()

  // Esc closes the panel. Capture-phase + stopPropagation so it doesn't also
  // trip the inbox's own Escape handler (which would deselect an email behind
  // the modal). The inbox hotkey handler also bails while this panel is open
  // (see InboxPage), so a stray 'c'/'d'/'j' can't archive/junk in the back.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      data-testid="email-shortcuts-panel"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative z-[1] flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-border bg-background shadow-2xl">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <Keyboard className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground">Keyboard shortcuts</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
            className={cn(
              'inline-flex h-7 w-7 items-center justify-center rounded-md',
              'text-muted-foreground hover:bg-accent hover:text-foreground transition-colors',
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-6 overflow-y-auto px-5 py-4">
          {/* Editable shortcuts — today these are the app-wide ones (command
              palette, settings, sidebar, search, save). Remap or turn any off;
              changes sync across devices and to Settings → Shortcuts. */}
          <ShortcutCustomizer
            overrides={overrides}
            loading={loading}
            error={error}
            onRemap={remap}
            onToggle={setEnabled}
            onReset={reset}
            onResetAll={resetAll}
          />

          <div className="border-t border-border" />

          {/* Read-only reference — defaults to the Email Hub tab (this page),
              with every other hub + the General/app-wide set a click away. */}
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-foreground">All shortcuts</h3>
            <p className="text-xs text-muted-foreground">
              Email Hub shortcuts for this page, plus every other hub. The
              editable ones are above; the rest become rebindable as each hub
              is migrated.
            </p>
          </div>
          <HubShortcutsReference defaultHub="email" />
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default KeyboardShortcutsPanel
